/**
 * /preview/native — non-destructive visual prototype of the proposed mobile
 * native-feel UI. This route does NOT touch the live Dashboard / Layout —
 * it's a pure mock so the user can decide before we change the real app.
 *
 * Implements the patterns from the user's request:
 *  - (a) Bottom tab bar (Dashboard / Events / Calendar / Chat / More)
 *  - (b) Standalone-mode polish (safe-area inset top/bottom, no big page title)
 *  - (c) App-style condensed sticky header (band name + avatar + bell)
 *  - (d) Pull-to-refresh affordance (visual only here)
 *  - (e/f/g) Touch polish + theme color + splash-style intro
 *
 * Frame the mock in an iPhone-style chassis on desktop so the user sees
 * exactly what installed-PWA users would see.
 */

import { Link } from "react-router-dom";
import {
  Home,
  CalendarDays,
  MessageCircle,
  Music,
  MoreHorizontal,
  Bell,
  Search,
  ChevronRight,
  Check,
} from "lucide-react";

const GOLD = "#E6B800";
const BG = "#0A0908";
const CARD = "#171513";
const MUTED = "#9CA29B";

function NativeMockShell({ children }) {
  // iPhone-style chassis so the mock looks "framed" on a desktop preview.
  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6" style={{ background: "#1a1815" }}>
      <div
        className="relative rounded-[44px] overflow-hidden border-[14px] border-[#0a0908] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
        style={{ width: "390px", height: "844px", background: BG }}
      >
        {/* Status bar simulator */}
        <div className="absolute top-0 inset-x-0 h-11 z-50 flex items-center justify-between px-6 text-[13px] font-semibold text-white">
          <span>9:41</span>
          <span className="flex items-center gap-1.5 opacity-90">
            <span>5G</span>
            <svg width="22" height="11" viewBox="0 0 22 11" fill="white">
              <rect x="0" y="0" width="18" height="11" rx="2" fill="none" stroke="white" />
              <rect x="2" y="2" width="14" height="7" fill="white" />
              <rect x="19" y="3" width="2" height="5" fill="white" />
            </svg>
          </span>
        </div>
        <div className="h-full overflow-y-auto" style={{ paddingTop: "44px" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function CondensedAppHeader() {
  return (
    <div
      className="sticky top-0 z-40 backdrop-blur-md border-b border-white/5 px-4 py-3 flex items-center justify-between"
      style={{ background: "rgba(10,9,8,0.85)" }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(230,184,0,0.15)" }}
        >
          <Music className="w-4 h-4" style={{ color: GOLD }} />
        </div>
        <div>
          <div className="text-[15px] font-semibold tracking-tight text-white">Vivo Test Band</div>
          <div className="text-[11px]" style={{ color: MUTED }}>Sun, May 31 · 1 upcoming</div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/5 active:bg-white/10 transition-colors">
          <Search className="w-4 h-4 text-white/80" />
        </button>
        <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/5 active:bg-white/10 transition-colors relative">
          <Bell className="w-4 h-4 text-white/80" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: GOLD }} />
        </button>
      </div>
    </div>
  );
}

function PullToRefreshHint() {
  return (
    <div className="flex justify-center py-3" style={{ color: MUTED }}>
      <span className="text-[11px] uppercase tracking-widest">↓ pull to refresh</span>
    </div>
  );
}

function StatPill({ label, value, accent }) {
  return (
    <div
      className="rounded-2xl p-3 flex flex-col gap-0.5"
      style={{ background: CARD, border: "1px solid rgba(255,255,255,0.05)" }}
    >
      <div className="text-[10px] uppercase tracking-wider" style={{ color: MUTED }}>{label}</div>
      <div className="text-2xl font-heading font-bold" style={{ color: accent || "white" }}>{value}</div>
    </div>
  );
}

function NextEventCard() {
  return (
    <div
      className="rounded-2xl p-4 mx-4 mt-3"
      style={{
        background: "linear-gradient(135deg, #1a1612, #171513)",
        border: "1px solid rgba(230,184,0,0.2)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Next up</span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa" }}>rehearsal</span>
      </div>
      <div className="text-[17px] font-semibold text-white mb-1">Test</div>
      <div className="text-[13px]" style={{ color: MUTED }}>Sun, May 31 · 7:00 PM · Test</div>
      <div className="flex items-center gap-2 mt-3">
        <span
          className="px-3 py-1.5 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
          style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80" }}
        >
          <Check className="w-3.5 h-3.5" /> You're going
        </span>
        <button className="text-[12px]" style={{ color: MUTED }}>Change response →</button>
      </div>
    </div>
  );
}

function ListItem({ title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 active:bg-white/5 transition-colors">
      <div className="min-w-0">
        <div className="text-[14px] text-white truncate">{title}</div>
        <div className="text-[12px] truncate" style={{ color: MUTED }}>{subtitle}</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        {right}
        <ChevronRight className="w-4 h-4" style={{ color: MUTED }} />
      </div>
    </div>
  );
}

function SectionHeading({ children, action }) {
  return (
    <div className="flex items-end justify-between px-4 mt-6 mb-2">
      <h3 className="text-[10px] uppercase tracking-widest" style={{ color: MUTED }}>{children}</h3>
      {action && <span className="text-[12px]" style={{ color: GOLD }}>{action}</span>}
    </div>
  );
}

function BottomTabBar({ active = "home" }) {
  const tabs = [
    { key: "home", label: "Home", Icon: Home },
    { key: "events", label: "Events", Icon: CalendarDays },
    { key: "music", label: "Music", Icon: Music },
    { key: "chat", label: "Chat", Icon: MessageCircle },
    { key: "more", label: "More", Icon: MoreHorizontal },
  ];
  return (
    <div
      className="absolute bottom-0 inset-x-0 z-40 backdrop-blur-md border-t border-white/5 flex items-stretch"
      style={{
        background: "rgba(10,9,8,0.92)",
        paddingBottom: "16px",
        paddingTop: "8px",
      }}
    >
      {tabs.map(({ key, label, Icon }) => {
        const isActive = key === active;
        return (
          <button
            key={key}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1 active:opacity-60 transition-opacity"
            style={{ color: isActive ? GOLD : MUTED }}
          >
            <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.4 : 2} />
            <span className="text-[10px] font-medium">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function NativePreview() {
  return (
    <NativeMockShell>
      <CondensedAppHeader />
      <PullToRefreshHint />

      {/* Welcome strip — much smaller than current "Vivo Admin" giant title */}
      <div className="px-4 mt-1">
        <div className="text-[22px] font-heading font-bold text-white tracking-tight">Hi, Vivo 👋</div>
        <div className="text-[13px]" style={{ color: MUTED }}>Free trial · 15 days remaining</div>
      </div>

      <NextEventCard />

      {/* Stats row — 4 pills horizontally scrollable on mobile */}
      <div className="grid grid-cols-2 gap-2 px-4 mt-5">
        <StatPill label="Players" value="9" />
        <StatPill label="Sections" value="33" />
        <StatPill label="Rehearsals" value="1" accent={GOLD} />
        <StatPill label="Concerts" value="0" />
      </div>

      <SectionHeading action="View all">Upcoming events</SectionHeading>
      <div className="mx-4 rounded-2xl overflow-hidden" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.05)" }}>
        <ListItem title="Test rehearsal" subtitle="Sun, May 31 · 7:00 PM" right={<span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80" }}>Going</span>} />
        <div className="border-t border-white/5" />
        <ListItem title="Summer concert" subtitle="Sat, Jun 14 · 7:30 PM" right={<span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(234,179,8,0.15)", color: GOLD }}>Maybe</span>} />
      </div>

      <SectionHeading action="All">Notifications</SectionHeading>
      <div className="mx-4 rounded-2xl overflow-hidden" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.05)" }}>
        <ListItem title="Test event audience" subtitle="Apr 19, 9:48 PM" right={null} />
        <div className="border-t border-white/5" />
        <ListItem title="Welcome to Vivo!" subtitle="Apr 18, 2:14 PM" right={null} />
      </div>

      {/* Bottom spacing so content doesn't sit under the tab bar */}
      <div style={{ height: "120px" }} />

      <BottomTabBar active="home" />

      {/* Diagnostic strip — visible only in this preview */}
      <div className="absolute -bottom-8 inset-x-0 text-center text-[10px] uppercase tracking-widest opacity-70" style={{ color: MUTED }}>
        preview · proposed mobile redesign
      </div>
    </NativeMockShell>
  );
}

/**
 * Side-by-side wrapper — current Dashboard on the left, proposed redesign on the right.
 * Used at /preview/native?compare=1 for an apples-to-apples comparison.
 */
export function NativePreviewCompare() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-start p-6 gap-4" style={{ background: "#1a1815" }}>
      <div className="text-center text-white max-w-3xl">
        <h1 className="text-2xl font-heading mb-2">Mobile UI: current vs proposed</h1>
        <p className="text-sm opacity-70">
          Right side = the redesign. If you like it, say the word and I'll port it into the live app
          (mobile-only · desktop sidebar untouched). 
          <Link to="/preview/native" className="ml-2 underline" style={{ color: GOLD }}>open the proposed view full-frame</Link>
        </p>
      </div>
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Current — embed the live dashboard via iframe so we always show the truth */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest opacity-70" style={{ color: "#9CA29B" }}>Current — live</span>
          <iframe
            src="/"
            title="current"
            className="rounded-[44px] border-[14px] border-[#0a0908]"
            style={{ width: "390px", height: "844px", background: BG }}
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest" style={{ color: GOLD }}>Proposed</span>
          <NativePreview />
        </div>
      </div>
    </div>
  );
}
